import {LoginContext} from 'components/LoginContext'
import {useQueryString} from 'hooks'
import _ from 'lodash'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useContext, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {staticPaths} from 'routes/staticPaths'
import {NullAsDefault} from 'views/Shared'
import SearchContext from './SearchContext'
import {copyLocationParam, getRouteSearchParams} from "../../envs/_current/history";

export const SearchTypes = Object.freeze({
    ALL: 'all',
    USER: 'user',
    POST: 'post',
    KEYWORD: 'keyword',
    DOCUMENT: 'document',
    ARTICLE: 'article',
    CHANNEL: 'channel',
    EVENT: 'event',
    PILGRIMAGE: 'pilgrimage',
    EXAMINATION: 'examination',
    EXAMINATION_PAPER: 'examination-paper',
    ASK: 'ask',
    UNDEFINED: 'undefined',
    PRODUCT: 'product',
    ORGANIZATION: 'organization',
    COURSE: 'course',
    MUSIC: 'music',
    CODI_DOCUMENT: 'codi-document',
    CODI_DOCUMENT_ROW: 'codi-document-row',
    LITURGY: 'liturgy',
    CHURCHES: 'masstimes',
    DIVINE_WORD: 'divine-word',
    PROFILES: 'profiles',
    SAINTS: 'saints',
    PRELATES: 'prelates',
    DIOCESES: 'dioceses',
    RELIGIOUS_ORDERS: 'devotion',
    FEEDBACK: 'feedback',
    CATECHISM: 'catechism',
    CANON_LAW: 'canon_law',
    VATICAN_II: 'vatican_ii',
    YELLOW_PAGE: 'yellow_pages',
    MISSALS: 'missals',
    BIBLES: 'bibles',
    DEFINITION: 'definition',
    ECCLESIASTIC: 'ecclesiastic',
    EVANGELIZATION: 'evangelization',
})

export const fromSearchTab = (type) => {
    switch (type) {
        case SearchTypes.ASK:
            return 'questions and answers'
        default:
            return type
    }
}

export const defaultSearchType = SearchTypes.ALL

export const SearchParams = Object.freeze({
    TAB: 'tab',
    TYPE: 'type',
    KEYWORD: 'keyword',
    CATEGORIES: 'categories',
    DOCUMENT_TYPES: 'document_types',
    MUSIC_TYPES: 'music_types',
    MUSIC_AUTHORS: 'music_authors',
})

const getSearchParams = (values) => {
    const types = Object.values(SearchParams)
    return types.reduce((arr, current) => {
        return {
            ...arr,
            [current]: _.get(values, current),
        }
    }, {})
}

const authenticatedTabOptions = [
    SearchTypes.ORGANIZATION,
    SearchTypes.LITURGY,
    // SearchTypes.CODI_DOCUMENT,
    SearchTypes.CODI_DOCUMENT_ROW,
    SearchTypes.KEYWORD,
    SearchTypes.ARTICLE,
    SearchTypes.ASK,
    SearchTypes.EVENT,
    SearchTypes.CHANNEL,
    SearchTypes.EXAMINATION,
    SearchTypes.COURSE,
]

const unauthenticatedTabOptions = [...authenticatedTabOptions]

const SearchProvider = ({children}) => {
    const location = useLocation()

    const login = useContext(LoginContext)

    const [current, setCurrent] = useState('')

    const [searchParams] = useQueryString(location.pathname)

    const history = useHistory()

    const searchTabOptions = login
        ? authenticatedTabOptions
        : unauthenticatedTabOptions

    const onSearch = ({type, keyword = '', categories = '', document_types = '', music_types = '', music_authors = ''}, animate = NullAsDefault) => {
        animate()
        const _searchParams = getRouteSearchParams()
        if (!_.isEmpty(type)) {
            _searchParams.set(
                SearchParams.TYPE,
                Object.values(SearchTypes).includes(type) ? type : defaultSearchType
            )
        }
        if (_.isEmpty(keyword.trim())) {
            _searchParams.delete(SearchParams.KEYWORD)
        } else {
            _searchParams.set(SearchParams.KEYWORD, keyword)
        }
        if (_.isEmpty(categories)) {
            _searchParams.delete(SearchParams.CATEGORIES)
        } else {
            _searchParams.set(SearchParams.CATEGORIES, categories)
        }
        if (_.isEmpty(document_types)) {
            _searchParams.delete(SearchParams.DOCUMENT_TYPES)
        } else {
            _searchParams.set(SearchParams.DOCUMENT_TYPES, document_types)
        }
        if (_.isEmpty(music_types)) {
            _searchParams.delete(SearchParams.MUSIC_TYPES)
        } else {
            _searchParams.set(SearchParams.MUSIC_TYPES, music_types)
        }
        if (_.isEmpty(music_authors)) {
            _searchParams.delete(SearchParams.MUSIC_AUTHORS)
        } else {
            _searchParams.set(SearchParams.MUSIC_AUTHORS, music_authors)
        }
        history.push({
            // ...location,
            ...copyLocationParam(location ?? {}),
            pathname: staticPaths.discovery.pathname,
            search: '?' + _searchParams.toString(),
        })
    }

    const onClear = () => {
        const _searchParams = getRouteSearchParams()
        if (_searchParams.has(SearchParams.KEYWORD)) {
            _searchParams.delete(SearchParams.KEYWORD)
        }
        history.replace({
            search: '?' + _searchParams.toString(),
        })
    }

    return (
        <SearchContext.Provider
            value={{
                current,
                onClear,
                onSearch,
                setCurrent,
                searchParams,
                getSearchParams,
                searchTabOptions,
            }}>
            {children}
        </SearchContext.Provider>
    )
}

export default SearchProvider
